/**
 * Wire → view-model mapping for the referee flow (WP-F4).
 *
 * The generated `@bymyword/api-types` document now TYPES the referee context 200 response
 * (`RefereeContextResponse`), so the wire shape is sourced from the generated type (aliased below)
 * rather than hand-declared — contract drift becomes a compile error. This module still owns the
 * one place where that flat, camelCased wire (snake_case status/step) is translated into the app's
 * own view-model (`RefereeContext`, `src/lib/api-types.ts`). Kept pure and transport-free so the
 * mapping — the crux of this work package — is unit-tested directly without a fetch.
 *
 * Contract facts pinned here (from the WP-B11 endpoints + integration tests):
 *  - The context wire is FLAT and camelCased; `status`/`step` are snake_case strings.
 *  - `status` is the FULL `reference_requests.status` machine (candidate-side states included), so
 *    the candidate-leg values (draft/requested/sent/viewed) collapse to the referee's `invited`,
 *    and `cancelled` is the referee's `withdrawn`.
 *  - The wire carries NO `referenceId`, candidate `headline`, `maxRecordingSeconds`,
 *    `redraftsRemaining`, `roleThen`, `verificationCode`, or `signedAt` — those are either derived,
 *    defaulted to the known product constants, or land with later work packages (WP-B14/B15).
 *  - This mapping CANNOT crash on a missing field: a partial context (e.g. a null candidate name)
 *    must degrade to placeholders, never a TypeError that masquerades as a 'network' error.
 */
import type {
  components,
  DeclineReasonToken,
  DraftState,
  DraftStatus,
  DraftWrite,
  RefereeContext,
  RefereeRelationship,
  ReferenceStatus,
} from './api-types';

/**
 * The wire shape of `GET /api/referee/context`, sourced from the generated OpenAPI document so a
 * backend contract change breaks the build here. (Alias kept for the mapping + its unit tests.)
 */
export type RefereeContextWire = components['schemas']['RefereeContextResponse'];

/** The wire shapes of the WP-B14 draft endpoints, likewise sourced from the generated document. */
export type DraftResponseWire = components['schemas']['DraftResponse'];
export type DraftWriteWire = components['schemas']['DraftWriteResponse'];

/** ~3-minute recording cap (ux-flows §2 step 5). The context wire does not carry it. */
export const DEFAULT_MAX_RECORDING_SECONDS = 180;
/** ~3 regenerations per reference (ux-flows post-synthesis §8). Not on the context wire. */
export const DEFAULT_REDRAFTS_REMAINING = 3;

/**
 * Full `reference_requests.status` → the referee-leg view-model status. The candidate-side states
 * the referee never sees collapse to `invited`; `cancelled` is the referee's `withdrawn`. An
 * unrecognised value falls back to `invited` (the calm pre-accept landing) rather than throwing.
 */
const STATUS_MAP: Record<string, ReferenceStatus> = {
  draft: 'invited',
  requested: 'invited',
  sent: 'invited',
  viewed: 'invited',
  accepted: 'accepted',
  declined: 'declined',
  recorded: 'recorded',
  processing: 'processing',
  drafted: 'drafted',
  signed: 'signed',
  expired: 'expired',
  cancelled: 'withdrawn',
  failed: 'failed',
  needs_review: 'needs_review',
  rerecord_requested: 'rerecord_requested',
};

export function mapRefereeStatus(wireStatus: string): ReferenceStatus {
  return STATUS_MAP[wireStatus] ?? 'invited';
}

const KNOWN_DECLINE_REASONS = new Set<DeclineReasonToken>([
  'no_time',
  'not_comfortable',
  'dont_know_well',
  'prefer_not',
]);

/** Keep only the decline tokens the SPA can render; drop anything unexpected from the wire. */
export function mapDeclineReasons(wire: readonly string[] | undefined): DeclineReasonToken[] {
  if (!wire) return [];
  return wire.filter((r): r is DeclineReasonToken => KNOWN_DECLINE_REASONS.has(r as DeclineReasonToken));
}

/**
 * Best-effort parse of the free-text relationship the candidate entered into the view-model enum,
 * so the confirm-details chip can pre-select. Unmatched-but-present text is `other`; blank is
 * undefined (the referee simply hasn't chosen a relationship yet).
 */
export function parseRelationship(value: string | null | undefined): RefereeRelationship | undefined {
  if (!value || value.trim().length === 0) return undefined;
  const v = value.trim().toLowerCase();
  if (v.includes('direct') && v.includes('report')) return 'direct_report';
  if (v.includes('manager') || v.includes('managed') || v.includes('supervis')) return 'manager';
  if (v.includes('colleague') || v.includes('co-worker') || v.includes('coworker') || v.includes('peer'))
    return 'colleague';
  if (v.includes('client') || v.includes('customer')) return 'client';
  if (v.includes('mentor')) return 'mentor';
  return 'other';
}

/**
 * Two-letter avatar initials from a display name (first + last word, else first two letters).
 * Null/blank in → `?` out (never throws — a partial context must degrade, not crash the mapping).
 */
export function initialsOf(name: string | null | undefined): string {
  const parts = (name ?? '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

/* ---------------------------------------------------------------------------------------------- *
 * Draft poll / write (WP-B14 → WP-F6)
 * ---------------------------------------------------------------------------------------------- */

// The draft counters used to arrive as `unknown` and were narrowed here by an `asInt` guard: the
// Api emitted int properties with a `pattern` + `format: int32` and no `"type"`, which
// openapi-typescript could only render as `unknown`. That is fixed at the source — the document
// now emits `type: integer`, so `version`, `regenerationsUsed` and `regenerationsRemaining`
// generate as real numbers and the guard became a no-op returning its own argument. It is gone
// rather than left dormant: a workaround kept past its bug reads as a warning that the bug is
// still there.

/**
 * The wire's `state` → the referee-facing draft status. An unrecognised value degrades to
 * `processing` — the same choice the backend itself makes for `failed`: "still working on it" is
 * the calm, honest answer, and it is the only one of the four that is safe to be wrong about
 * (it withholds the letter and keeps polling rather than showing something it shouldn't).
 */
const DRAFT_STATE_MAP: Record<string, DraftStatus> = {
  processing: 'processing',
  needs_review: 'needs_review',
  rerecord_requested: 'rerecord_requested',
  drafted: 'drafted',
};

export function mapDraftStatus(wireState: string): DraftStatus {
  return DRAFT_STATE_MAP[wireState] ?? 'processing';
}

/**
 * Map the draft poll wire to the view-model.
 *
 * Two contract facts are enforced HERE rather than trusted from the wire:
 *  - **The letter rides on the STATE.** The backend already withholds `letterBody` unless the
 *    state is `drafted`; this re-applies that rule client-side so a future/looser payload can
 *    never surface a `needs_review` letter that a human has not cleared. Belt and braces on the
 *    one field where being wrong defeats the moderation divert.
 *  - **`keyPoints` may legitimately be EMPTY** (the `no_key_points` divert: extraction found
 *    nothing usable, so the drafter was never called and there is no letter at all). That is a
 *    normal shape, not a broken payload — hence no defaulting or throwing.
 *
 * The transcript is ONE string; the wire carries no timestamps, so the mock's "0:04" gutter is not
 * renderable and is deliberately not faked. Blank/whitespace collapses to `undefined` so the
 * screens can branch on presence without testing for ''.
 */
export function mapDraftResponse(wire: DraftResponseWire): DraftState {
  const status = mapDraftStatus(wire.state);
  const drafted = status === 'drafted';
  const transcript = wire.transcript?.trim();

  return {
    status,
    keyPoints: wire.keyPoints ?? [],
    transcript: transcript ? transcript : undefined,
    letterBody: drafted && wire.letterBody ? wire.letterBody : undefined,
    // `version` is the one nullable counter on the wire, and `|| undefined` (not `?? undefined`)
    // is deliberate: a null and a 0 both mean "no version yet", and the screens branch on
    // presence.
    version: drafted ? (wire.version || undefined) : undefined,
    regenerationsUsed: wire.regenerationsUsed,
    regenerationsRemaining: wire.regenerationsRemaining,
  };
}

/** Map an edit/regenerate write echo. Only reached on a 200, so the letter is always present. */
export function mapDraftWrite(wire: DraftWriteWire): DraftWrite {
  return {
    letterBody: wire.letterBody,
    version: wire.version,
    regenerationsUsed: wire.regenerationsUsed,
    regenerationsRemaining: wire.regenerationsRemaining,
  };
}

/** Map the flat context wire to the app view-model the screens render. */
export function mapRefereeContext(wire: RefereeContextWire): RefereeContext {
  // Defend against a partial context: the wire types these as non-null, but a truncated/legacy
  // payload could omit them, and a raw `.trim()` on null would throw a TypeError that the transport
  // then mislabels a 'network' failure. Default to '' so the screens show calm placeholders instead.
  const candidateName = wire.candidateName ?? '';
  const refereeName = wire.refereeName ?? '';
  return {
    // The context wire does not expose the opaque reference id; steps 0–3 never display it.
    referenceId: '',
    status: mapRefereeStatus(wire.status),
    candidate: {
      name: candidateName,
      initials: initialsOf(candidateName),
      photoUrl: wire.candidateAvatarUrl ?? undefined,
      note: wire.personalNote ?? undefined,
      // The context wire carries no candidate headline; the landing simply omits it.
    },
    referee: {
      name: refereeName,
      relationship: parseRelationship(wire.relationshipContext),
      company: wire.companyName ?? undefined,
      // No `roleThen` on the wire — referee_contacts has no title column (WP-B11 contract).
    },
    maxRecordingSeconds: DEFAULT_MAX_RECORDING_SECONDS,
    redraftsRemaining: DEFAULT_REDRAFTS_REMAINING,
    declineReasons: mapDeclineReasons(wire.declineReasons),
    // The CURRENT attestation the consent POST must echo (422 on a stale version). Nullable on the
    // wire → undefined here; `recordConsent` falls back to the default doc/version when absent.
    consentDocumentKey: wire.consentDocumentKey ?? undefined,
    consentDocumentVersion: wire.consentDocumentVersion ?? undefined,
  };
}
